import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  Building, 
  User, 
  Hash, 
  MapPin, 
  DollarSign,
  QrCode,
  Upload,
  Save,
  Trash2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
// Create API configuration directly in this component
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

const api = {
  get: (url, config) => axios.get(`${API_BASE_URL}${url}`, config),
  post: (url, data, config) => axios.post(`${API_BASE_URL}${url}`, data, config),
  put: (url, data, config) => axios.put(`${API_BASE_URL}${url}`, data, config),
  delete: (url, config) => axios.delete(`${API_BASE_URL}${url}`, config)
};

// Debug: Check if api is properly configured
console.log('API configuration check:', { 
  api, 
  apiType: typeof api, 
  hasGet: !!api?.get,
  baseURL: API_BASE_URL,
  isCustomAPI: 'Yes'
});

const BankDetailsForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    ifscCode: '',
    accountType: 'Savings',
    upiId: ''
  });
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [existingQrCode, setExistingQrCode] = useState(null);
  const [hasExistingDetails, setHasExistingDetails] = useState(false);

  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        console.log('=== fetchBankDetails called ===');
        console.log('API object:', api);
        console.log('API type:', typeof api);
        console.log('API methods:', Object.keys(api || {}));
        console.log('API base URL:', API_BASE_URL);
        
        setLoading(true);
        const token = localStorage.getItem('ngoToken');
        console.log('NGO token from localStorage:', token ? 'Token found' : 'No token');
        
        if (!token) {
          toast.error('Authentication required');
          navigate('/ngo/login');
          return;
        }
        
        // Use the configured API endpoint
        let response;
        try {
          console.log('Fetching bank details from configured API endpoint');
          
          response = await api.get('/bank-details', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token
            }
          });
          
          console.log('Bank details fetched successfully:', response.data);
        } catch (err) {
          console.error('Error fetching bank details:', err);
          throw err;
        }
        
        // If we got a successful response, use the data
        if (response && response.data && response.data.success) {
          setError(null); // Clear any previous errors
          const details = response.data.data;
          setBankDetails({
            accountHolderName: details.accountHolderName || '',
            accountNumber: details.accountNumber || '',
            bankName: details.bankName || '',
            branchName: details.branchName || '',
            ifscCode: details.ifscCode || '',
            accountType: details.accountType || 'Savings',
            upiId: details.upiId || ''
          });
          
          if (details.qrCodeImage) {
            // Ensure the QR code image has the full URL
            const baseUrl = response.config.url.split('/api')[0];
            const qrCodeUrl = details.qrCodeImage.startsWith('http') 
              ? details.qrCodeImage 
              : `${baseUrl}${details.qrCodeImage}`;
            
            console.log('QR code image URL:', qrCodeUrl);
            setExistingQrCode(qrCodeUrl);
          }
          
          setHasExistingDetails(true);
        } else if (response && response.status === 404) {
          // No bank details exist yet - this is normal for new users
          console.log('No bank details found - this is normal for new users');
          setError(null);
          setHasExistingDetails(false);
        }
      } catch (error) {
        console.error('Error fetching bank details:', error);
        
        if (error.response) {
          // Server responded with error status
          if (error.response.status === 404) {
            // No bank details exist yet - this is normal
            console.log('No bank details found - this is normal for new users');
            setError(null);
            setHasExistingDetails(false);
          } else if (error.response.status === 401) {
            setError('Authentication failed. Please login again.');
            toast.error('Authentication failed');
            navigate('/ngo/login');
          } else if (error.response.status === 500) {
            setError('Server error. Please try again later.');
            toast.error('Server error');
          } else {
            setError(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
            toast.error('Failed to load bank details');
          }
        } else if (error.request) {
          // Request was made but no response received
          setError('No response from server. Please check if the server is running.');
          toast.error('Server not responding');
        } else {
          // Something else happened
          setError(`Error: ${error.message}`);
          toast.error('Failed to load bank details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous errors when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleQrCodeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrCodeFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrCodePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError(null);
    
    try {
      setLoading(true);
      const token = localStorage.getItem('ngoToken');
      
      if (!token) {
        toast.error('Authentication required');
        navigate('/ngo/login');
        return;
      }
      
      // Create form data
      const formData = new FormData();
      formData.append('accountHolderName', bankDetails.accountHolderName);
      formData.append('accountNumber', bankDetails.accountNumber);
      formData.append('bankName', bankDetails.bankName);
      formData.append('branchName', bankDetails.branchName);
      formData.append('ifscCode', bankDetails.ifscCode);
      formData.append('accountType', bankDetails.accountType);
      formData.append('upiId', bankDetails.upiId);
      
      if (qrCodeFile) {
        formData.append('qrCodeImage', qrCodeFile);
      }
      
        // Use the configured API endpoint
        let response;
        try {
          console.log('Updating bank details at configured API endpoint');
          
          response = await api.post('/bank-details/update', formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          console.log('Bank details updated successfully:', response.data);
        } catch (err) {
          console.error('Error updating bank details:', err);
          throw err;
        }
      
                if (response && response.data && response.data.success) {
          toast.success('Bank details updated successfully');
          setError(null); // Clear any previous errors
        
        // Update state with new data
        const details = response.data.data;
        setBankDetails({
          accountHolderName: details.accountHolderName || '',
          accountNumber: details.accountNumber || '',
          bankName: details.bankName || '',
          branchName: details.branchName || '',
          ifscCode: details.ifscCode || '',
          accountType: details.accountType || 'Savings',
          upiId: details.upiId || ''
        });
        
        if (details.qrCodeImage) {
          setExistingQrCode(details.qrCodeImage);
        }
        
        setHasExistingDetails(true);
        setQrCodeFile(null);
        setQrCodePreview(null);
      } else {
        toast.error('Failed to update bank details');
      }
    } catch (error) {
      console.error('Error updating bank details:', error);
      setError(`Failed to update bank details: ${error.message}`);
      toast.error('Failed to update bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete your bank details? This action cannot be undone.')) {
      // Clear any previous errors
      setError(null);
      
      try {
        setLoading(true);
        const token = localStorage.getItem('ngoToken');
        
        if (!token) {
          toast.error('Authentication required');
          navigate('/ngo/login');
          return;
        }
        
        // Use the configured API endpoint
        let response;
        try {
          console.log('Deleting bank details at configured API endpoint');
          
          response = await api.delete('/bank-details', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token
            }
          });
          
          console.log('Bank details deleted successfully');
        } catch (err) {
          console.error('Error deleting bank details:', err);
          throw err;
        }
        
        if (response && response.data && response.data.success) {
          toast.success('Bank details deleted successfully');
          setError(null); // Clear any previous errors
          
          // Reset form
          setBankDetails({
            accountHolderName: '',
            accountNumber: '',
            bankName: '',
            branchName: '',
            ifscCode: '',
            accountType: 'Savings',
            upiId: ''
          });
          
          setExistingQrCode(null);
          setQrCodeFile(null);
          setQrCodePreview(null);
          setHasExistingDetails(false);
        } else {
          toast.error('Failed to delete bank details');
        }
      } catch (error) {
        console.error('Error deleting bank details:', error);
        setError(`Failed to delete bank details: ${error.message}`);
        toast.error('Failed to delete bank details');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">
          {hasExistingDetails ? 'Loading bank details...' : 'Initializing form...'}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {hasExistingDetails ? 'Update Bank Details' : 'Add Bank Details'}
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          {hasExistingDetails 
            ? 'Update your bank account information for receiving donations' 
            : 'Add your bank account information to start receiving donations'}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">Error</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account Holder Name */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                Account Holder Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="accountHolderName"
                  value={bankDetails.accountHolderName}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter account holder name"
                />
              </div>
            </div>

            {/* Account Number */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                Account Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CreditCard size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="accountNumber"
                  value={bankDetails.accountNumber}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter account number"
                />
              </div>
            </div>

            {/* Bank Name */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                Bank Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="bankName"
                  value={bankDetails.bankName}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter bank name"
                />
              </div>
            </div>

            {/* Branch Name */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                Branch Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="branchName"
                  value={bankDetails.branchName}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter branch name"
                />
              </div>
            </div>

            {/* IFSC Code */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                IFSC Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Hash size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="ifscCode"
                  value={bankDetails.ifscCode}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter IFSC code"
                />
              </div>
            </div>

            {/* Account Type */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                Account Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign size={18} className="text-gray-400" />
                </div>
                <select
                  name="accountType"
                  value={bankDetails.accountType}
                  onChange={handleChange}
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="Savings">Savings</option>
                  <option value="Current">Current</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* UPI ID */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                UPI ID
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <QrCode size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="upiId"
                  value={bankDetails.upiId}
                  onChange={handleChange}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter UPI ID (optional)"
                />
              </div>
            </div>
          </div>

          {/* QR Code Upload */}
          <div className="mt-6">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
              Payment QR Code
            </label>
            <div className="mt-2 flex flex-col md:flex-row items-start gap-4">
              {/* QR Code Preview */}
              <div className="w-full md:w-1/3">
                {(qrCodePreview || existingQrCode) ? (
                  <div className="relative mb-4 bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-700 mb-2">QR Code Preview</div>
                    <div className="bg-white p-2 border border-gray-100 rounded-md">
                      <img 
                        src={qrCodePreview || existingQrCode} 
                        alt="QR Code Preview" 
                        className="w-full h-auto max-h-64 object-contain"
                        onError={(e) => {
                          console.error('Error loading QR code image');
                          e.target.src = 'https://via.placeholder.com/200x200?text=QR+Code+Error';
                          e.target.alt = 'Failed to load QR code';
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-gray-500">This QR code will be shown to donors</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (existingQrCode && !qrCodePreview) {
                            // If removing existing QR code, just hide it in UI but don't clear from DB yet
                            setExistingQrCode(null);
                          } else {
                            // If removing a new upload
                            setQrCodePreview(null);
                            setQrCodeFile(null);
                            document.getElementById('qrCodeUpload').value = '';
                          }
                        }}
                        className="text-red-600 hover:text-red-800 flex items-center"
                        title="Remove QR Code"
                      >
                        <Trash2 size={14} className="mr-1" />
                        <span className="text-xs">Remove</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 border-2 border-gray-200 border-dashed rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="text-center px-4">
                      <QrCode size={32} className="mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No QR code uploaded</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="w-full md:w-2/3">
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="qrCodeUpload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload size={24} className="text-gray-500 dark:text-gray-400 mb-2" />
                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG, JPG or JPEG (MAX. 5MB)
                      </p>
                    </div>
                    <input
                      id="qrCodeUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleQrCodeChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Upload a QR code image for easy payments. This will be shown to donors.
                </p>
              </div>
            </div>
          </div>

          {/* Information Note */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Information
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-200">
                  <p>
                    Your bank details will be used to receive donations for your projects. 
                    Make sure all information is accurate. The QR code will be displayed to donors 
                    for easy payment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            {hasExistingDetails && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-gray-700 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              {loading ? (
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              ) : (
                <Save size={16} className="mr-2" />
              )}
              {hasExistingDetails ? 'Update Details' : 'Save Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankDetailsForm;
